import { ArrowRight } from "lucide-react";
import type { NewsListComponent } from "../../types";
import { RuntimeLink } from "../../runtime/RendererRuntime";
import { resolveLinkHref } from "../../utils/links";

/** Vertical news list renderer with call-to-action link. */
export function NewsListRenderer({ component }: { component: NewsListComponent }) {
  const { items, ctaLabel, ctaHref, useGrayPanel } = component.content;
  const resolvedCtaHref = resolveLinkHref(ctaHref);
  const showGrayPanel = useGrayPanel === true || String(useGrayPanel) === "true";

  return (
    <section className="space-y-6">
      <div className="space-y-4">
        {items.map((item, index) => {
          const resolvedHref = resolveLinkHref(item.href);
          const panelClass = showGrayPanel ? "bg-[#F5F5F5]" : "bg-transparent";
          const content = (
            <div className={`rounded-sm ${panelClass} px-4 py-4 md:px-6 md:py-5`}>
              {item.tag && (
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#00B862]">
                  {item.tag}
                </div>
              )}
              <h3 className="mt-2 text-[18px] xl:text-[20px] font-semibold leading-snug text-[#00B862] md:text-[20px]">
                {item.title}
              </h3>
              {item.excerpt && (
                <p className="mt-2 text-sm xl:text-base leading-6 text-slate-600">{item.excerpt}</p>
              )}
              {item.meta && <p className="mt-2 text-sm xl:text-base text-slate-600">{item.meta}</p>}
              {item.date && <p className="mt-2 text-xs xl:text-sm text-slate-500">{item.date}</p>}
            </div>
          );

          if (resolvedHref) {
            return (
              <RuntimeLink key={`${item.title}-${index}`} href={resolvedHref} className="block">
                {content}
              </RuntimeLink>
            );
          }

          return (
            <div key={`${item.title}-${index}`} className="block">
              {content}
            </div>
          );
        })}
      </div>

      {resolvedCtaHref && ctaLabel ? (
        <RuntimeLink
          href={resolvedCtaHref}
          className="inline-flex items-center gap-2 text-sm xl:text-base font-semibold text-[#00B862] hover:text-[#007EC0]"
        >
          {ctaLabel}
          <ArrowRight className="h-4 w-4 text-[#FFC105]" />
        </RuntimeLink>
      ) : null}
    </section>
  );
}
